/**
 * A developer's day, as text: the report somebody is otherwise asked to write by hand.
 *
 * Two shapes. Markdown, to paste into a chat or an email to a manager — it reads as prose and
 * keeps its structure in Slack, Teams and most mail clients. CSV, for whoever keeps a spreadsheet.
 *
 * Everything here is a pure function of the day the API returned, so what is copied is exactly
 * what is on the screen: no second query, no figure computed differently in the export.
 */

import type { DaySession, DeveloperDay } from './types'
import { formatCost, formatDuration, formatNumber, formatTime } from './format'
import type { Locale } from './i18n'

type Translate = (key: string, vars?: Record<string, string | number>) => string

/**
 * What a session was about, in one line: the task, the stated focus, the spec — else the first note or step the
 * agent reported, so a page of queue runs does not read "Sem descrição reportada" eleven times (41, A13).
 */
export function subjectOf(session: DaySession, t: Translate): string {
  if (session.task) return `${session.task.externalId} — ${session.task.title}`
  if (session.focus) return session.focus
  if (session.spec) return `${session.spec.key} — ${session.spec.title}`
  const reported = session.notes?.[0] ?? session.steps?.[0]?.title
  if (reported) return reported.length > 90 ? `${reported.slice(0, 89)}…` : reported
  return t('day.noSubject')
}

function categoryLabel(session: DaySession, t: Translate): string {
  if (session.category === 'Spec' && session.spec) return session.spec.kind === 'Prd' ? 'PRD' : 'SDD'
  return t(`day.category.${session.category}`)
}

/** Markdown for a chat message or an email. Headings and bullets only: it has to survive pasting. */
export function dayMarkdown(day: DeveloperDay, dateLabel: string, t: Translate, locale: Locale): string {
  const totals = day.totals
  const lines: string[] = []

  lines.push(`## ${t('day.reportTitle', { name: day.displayName, date: dateLabel })}`)
  lines.push('')

  const headline = [
    `${t('day.time')}: ${formatDuration(totals.activeSeconds, locale)}`,
    `${t('day.sessions')}: ${totals.sessions}`,
    `${t('day.lines')}: +${formatNumber(totals.linesAdded, locale)} −${formatNumber(totals.linesDeleted, locale)}`,
    `${t('day.commits')}: ${totals.commits}`,
  ]
  if (totals.testsRun > 0) headline.push(`${t('day.tests')}: ${totals.testsPassed}/${totals.testsRun}`)
  if (totals.prompts > 0) headline.push(`${t('day.prompts')}: ${totals.prompts}`)
  lines.push(headline.join(' · '))

  if (totals.repositories.length > 0) lines.push(`${t('day.repositories')}: ${totals.repositories.join(', ')}`)

  for (const session of day.sessions) {
    lines.push('')

    const context = [categoryLabel(session, t)]
    if (session.spec && session.category !== 'Spec') context.push(`${session.spec.kind === 'Prd' ? 'PRD' : 'SDD'} ${session.spec.key}`)
    if (session.projectName) context.push(session.projectName)

    lines.push(`### ${subjectOf(session, t)} (${context.join(' · ')})`)

    const when = session.endedAt
      ? `${formatTime(session.startedAt, locale)}–${formatTime(session.endedAt, locale)}`
      : `${formatTime(session.startedAt, locale)}–…`
    const facts = [`${when} (${formatDuration(session.secondsInDay, locale)})`]
    if (session.repositories.length > 0) facts.push(session.repositories.join(', '))
    if (session.linesAdded + session.linesDeleted > 0) facts.push(`+${session.linesAdded} −${session.linesDeleted}`)
    if (session.testsRun > 0) facts.push(`${t('day.tests')} ${session.testsPassed}/${session.testsRun}`)
    lines.push(`- ${facts.join(' · ')}`)

    if (session.summary) lines.push(`- ${t('day.result')}: ${session.summary}`)

    const done = session.steps.filter((s) => s.status === 'Completed').map((s) => s.title)
    if (done.length > 0) lines.push(`- ${t('day.stepsDone')}: ${done.join('; ')}`)

    const open = session.steps.filter((s) => s.status === 'InProgress' || s.status === 'Pending').map((s) => s.title)
    if (open.length > 0 && session.status !== 'Completed') lines.push(`- ${t('day.stepsOpen')}: ${open.join('; ')}`)

    for (const note of session.notes.slice(-5)) lines.push(`- ${note}`)

    for (const commit of session.commits) {
      lines.push(`- ${t('day.commit')} ${commit.sha ?? ''} ${commit.message ?? ''}`.trimEnd())
    }

    for (const blocker of session.blockers) lines.push(`- ${t('day.blocker')}: ${blocker}`)
  }

  return lines.join('\n')
}

const CSV_COLUMNS = [
  'date', 'developer', 'category', 'task', 'subject', 'spec', 'project', 'repositories', 'start', 'end',
  'minutes', 'lines_added', 'lines_deleted', 'files', 'commits', 'tests_passed', 'tests_run', 'prompts',
  'input_tokens', 'output_tokens', 'cost_usd', 'cost_accuracy', 'status', 'summary',
] as const

/** RFC 4180: quote everything that needs it, double the quotes inside. */
function cell(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return ''
  const text = String(value)
  return /[",\n\r;]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text
}

/** One row per session. Numbers stay numbers, in the invariant format a spreadsheet parses. */
export function dayCsv(day: DeveloperDay, date: string): string {
  const rows = day.sessions.map((s) => [
    date,
    day.displayName,
    s.category,
    s.task?.externalId ?? '',
    s.task?.title ?? s.focus ?? '',
    s.spec?.key ?? '',
    s.projectName ?? '',
    s.repositories.join(' '),
    s.startedAt,
    s.endedAt ?? '',
    Math.round(s.secondsInDay / 60),
    s.linesAdded,
    s.linesDeleted,
    s.filesChanged,
    s.commits.length,
    s.testsPassed,
    s.testsRun,
    s.prompts,
    s.inputTokens,
    s.outputTokens,
    s.costUsd ?? '',
    s.costAccuracy,
    s.status,
    s.summary ?? '',
  ].map(cell).join(','))

  return [CSV_COLUMNS.join(','), ...rows].join('\r\n')
}

/** The day's cost, or a dash when no provider said. Its accuracy is shown beside it, not inside it. */
export function costLine(day: DeveloperDay, locale: Locale): string {
  if (day.totals.costAccuracy === 'Unavailable' || day.totals.costUsd == null) return '—'
  return formatCost(day.totals.costUsd, locale)
}
